// Lifted Marketing — businesses, affiliates, creators, and social promotion.
// Inquiries persist before any email attempt; creator showcase is opt-in
// and admin-approved.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Video, Target, ChevronRight, Sparkles, AlertTriangle } from "lucide-react";
import PageMeta from "@/components/PageMeta";
import { AUDIENCES, SERVICES } from "@/components/marketing/marketingConstants";
import { BusinessInquiryForm } from "@/components/marketing/BusinessInquiryForm";
import { CreatorInquiryForm } from "@/components/marketing/CreatorInquiryForm";
import { CreatorDirectory } from "@/components/marketing/CreatorDirectory";
import { MyCreatorProfiles } from "@/components/marketing/MyCreatorProfiles";

const BUSINESS_SERVICES = [
  { icon: Target, title: "Campaign planning", copy: "Strategy sessions, launch calendars, measurable goals." },
  { icon: Users, title: "Creator connections", copy: "Hand-matched creators by niche, audience, and budget." },
  { icon: Video, title: "Social content", copy: "Coordinate shoots, posts, and ongoing content pipelines." },
];
const PROCESS_STEPS = ["Goals", "Scope", "Creator matching", "Content", "Results review"];

function Hero({ pick }) {
  return (
    <section className="relative border-b border-white/5" data-testid="marketing-hero">
      <div className="relative w-full overflow-hidden bg-black">
        <img src="/images/covers/lifted-marketing.webp" alt="Lifted Marketing — astronaut creator at a desk connected to social platforms around the globe"
          className="w-full max-w-[1200px] mx-auto block h-auto aspect-[16/9] object-contain" fetchpriority="high" data-testid="marketing-cover-image" />
      </div>
      <div className="container mx-auto px-4 py-10 lg:py-14">
        <h1 className="font-anton uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] max-w-4xl">
          <span className="sr-only">Lifted Marketing — </span>
          Grow Your Brand. <span className="text-lifted-green italic">Build Your Audience.</span> Get Seen.
        </h1>
        <p className="text-[#efe9dc]/75 mt-5 text-base md:text-lg max-w-2xl">
          Marketing and promotional opportunities for businesses, affiliates, influencers, and independent creators.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button onClick={() => pick("social_promotion")} className="btn-lifted" data-testid="cta-promote-socials">Promote My Socials <ChevronRight className="h-3 w-3" /></button>
          <button onClick={() => pick("creator")} className="btn-ghost" data-testid="cta-apply-creator">Apply as a Creator</button>
          <button onClick={() => pick("business")} className="btn-ghost" data-testid="hero-market-cta">Grow My Brand</button>
          <a href="#creators" className="btn-ghost" data-testid="cta-explore-creators">Explore Creators</a>
        </div>
      </div>
    </section>
  );
}

function AudienceCards({ active, pick }) {
  return (
    <section className="py-14" data-testid="audience-cards">
      <div className="container mx-auto px-4">
        <div className="eyebrow text-lifted-green mb-2">Who it's for</div>
        <h2 className="font-anton uppercase text-3xl lg:text-4xl text-white mb-8">Choose your <span className="italic text-lifted-green">lane</span></h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            const on = active === a.key;
            return (
              <button key={a.key} onClick={() => pick(a.key)} aria-pressed={on} data-testid={`audience-card-${a.key}`}
                style={{ animationDelay: `${i * 70}ms` }}
                className={`text-left p-6 rounded-xl border transition-[transform,border-color,background-color] duration-200 hover:-translate-y-1 animate-in fade-in slide-in-from-bottom-2 ${
                  on ? "border-lifted-green bg-lifted-green/[0.07]" : "border-white/10 bg-white/[0.02] hover:border-white/30"}`}>
                <div className="flex items-center justify-between">
                  <Icon className="h-7 w-7 text-lifted-green" />
                  <span className="font-anton text-white/15 text-3xl">0{i + 1}</span>
                </div>
                <h3 className="font-bebas uppercase tracking-[0.14em] text-lg text-white mt-4">{a.title}</h3>
                <p className="text-sm text-white/65 mt-2 leading-snug">{a.copy}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-lifted-green">{a.cta} <ChevronRight className="h-3 w-3" /></span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="py-16 bg-black/30 border-y border-white/5" data-testid="services-section">
      <div className="container mx-auto px-4 grid lg:grid-cols-[1fr_1.2fr] gap-10">
        <div>
          <div className="eyebrow text-lifted-green">For businesses</div>
          <h2 className="font-anton uppercase text-3xl text-white mt-2 mb-6">Our <span className="italic text-lifted-green">Services</span></h2>
          <div className="space-y-3">
            {BUSINESS_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="glass-panel p-5 flex gap-4" data-testid={`service-${i}`}>
                  <Icon className="h-6 w-6 text-lifted-green shrink-0" />
                  <div><h3 className="font-bebas uppercase tracking-[0.18em] text-white">{s.title}</h3><p className="text-sm text-white/65 mt-1">{s.copy}</p></div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div className="eyebrow text-lifted-green">For creators & affiliates</div>
          <h2 className="font-anton uppercase text-3xl text-white mt-2 mb-6">Promotional <span className="italic text-lifted-green">Requests</span></h2>
          <ul className="grid sm:grid-cols-2 gap-2" data-testid="promo-services-list">
            {SERVICES.map(([k, l]) => (
              <li key={k} className="text-sm text-white/80 border border-white/10 rounded-lg px-4 py-3 bg-white/[0.02]">{l}</li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-[#ff5a4e] flex items-start gap-2" data-testid="services-disclaimer">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            All services are requests subject to approval and availability. Lifted does not guarantee followers, reach, engagement, or collaborations.
          </p>
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="py-14">
      <div className="container mx-auto px-4">
        <div className="eyebrow text-lifted-green">How we work</div>
        <h2 className="font-anton uppercase text-3xl text-white mt-2 mb-8">Our <span className="italic text-lifted-green">Process</span></h2>
        <div className="flex flex-wrap gap-3" data-testid="process-steps">
          {PROCESS_STEPS.map((s, i) => (
            <div key={s} className="glass-panel px-4 py-3 min-w-[150px]">
              <div className="text-[9px] text-lifted-green font-bebas tracking-[0.2em] uppercase">Step {i + 1}</div>
              <div className="text-white text-sm mt-1 font-bebas tracking-wider uppercase">{s}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LiftedMarketing() {
  const [audience, setAudience] = useState("business");
  const pick = (a) => {
    setAudience(a);
    setTimeout(() => document.getElementById("inquire")?.scrollIntoView({ behavior: "smooth" }), 30);
  };
  const meta = AUDIENCES.find((a) => a.key === audience);

  return (
    <div className="min-h-screen bg-[#04070a] text-white" data-testid="lifted-marketing-page">
      <PageMeta title="Lifted Marketing — Grow Your Brand. Build Your Audience. Get Seen."
        description="Marketing and promotional opportunities for businesses, affiliates, influencers, and independent creators."
        path="/lifted-marketing" />
      <Hero pick={pick} />
      <AudienceCards active={audience} pick={pick} />
      <ServicesSection />
      <Process />
      <section id="inquire" className="py-16 lg:py-20 border-t border-white/5" data-testid="inquiry-form-section">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="mb-6">
            <Sparkles className="h-7 w-7 text-lifted-green mb-3" />
            <h2 className="font-anton uppercase text-3xl text-white">Let's <span className="italic text-lifted-green">Talk</span></h2>
            <p className="text-white/60 text-sm mt-2">Selected: <span className="text-lifted-green" data-testid="selected-audience">{meta?.title}</span></p>
            <div className="flex flex-wrap gap-1.5 mt-3" role="tablist">
              {AUDIENCES.map((a) => (
                <button key={a.key} role="tab" aria-selected={audience === a.key} onClick={() => setAudience(a.key)} data-testid={`audience-tab-${a.key}`}
                  className={`text-[10px] font-bebas uppercase tracking-[0.18em] px-3 py-1.5 rounded border ${audience === a.key ? "bg-lifted-green text-black border-lifted-green" : "border-white/15 text-white/70"}`}>
                  {a.title}
                </button>
              ))}
            </div>
          </div>
          {audience === "business" ? <BusinessInquiryForm /> : <CreatorInquiryForm key={audience} audience={audience} />}
          <div className="mt-6 text-xs text-white/50">
            Lifted affiliate? <Link to="/affiliate-zone" className="text-lifted-green hover:underline" data-testid="affiliate-zone-link">Log in or enroll in the affiliate program</Link>.
            {" "}Supplies partner? <Link to="/apply/partner-store" className="text-lifted-green hover:underline">Apply here</Link>.
          </div>
        </div>
      </section>
      <MyCreatorProfiles />
      <CreatorDirectory onApply={() => pick("creator")} />
    </div>
  );
}
